<script>
	import LoadingIndicator from '@Components/LoadingIndicator.svelte'
	import { onMount, getContext, onDestroy } from 'svelte'
	const { getController, getEditorCanvas, getConfig, getRenderer, getEditorState } = getContext('editor')
	import { deconstructHoleId } from '@Utils/DataController.js'
	import { getIconHtml } from '@Utils/Icons.js'
	import { setRenderProperties, getRenderProperties } from '@Utils/Properties.js'
	import { GreenEditor } from '@GreenEditor/GreenEditor.js'
	import { disable } from 'ol/rotationconstraint.js'
	import Service from '@Renderer/Service.js'
	export let visible = true
	export let holeId = null
	let currentData = []
	let holeRenderer = getRenderer()
	let colorEnabled = false
	let textColorEnabled = false
	let editorState = getEditorState()
	let imageWidthEnabled = false
	let widthEndabled = false
	let tidyRotationEnabled = false
	let currentTextColor = 'black'
	let currentColor = 'black'
	let currentImageWidth = 30
	let currentWidth = 50
	let controller = getController()
	let config = getConfig()
	let editorCanvas = getEditorCanvas()
	let canvas
	let isLoading = false
	let backdropCanvas
	let imageSize = import.meta.env.VITE_RENDER_WIDTH
	let canvasSize = 700
	let greenEditor
	let holeData = null
	let currentTool = null
	let selectedObject = null
	let imageData = {}
	let ratio = imageSize / canvasSize
	$: isDarkMode = window.isDarkMode
	window.addEventListener('colorSchemeChanged', (e) => {
		isDarkMode = e.isDarkMode
	})
	var zones = []
	function isPointAdded(zoneId) {
		if (greenEditor) {
			return greenEditor.getObject(zoneId) !== null
		}
	}
	async function render() {
		isLoading = true
		greenEditor.disable()
		try {
			let h = deconstructHoleId(holeId)
			let theme = 'park'
			if (h) {
				theme = controller.getGroupSetting(h.holeGroupId, 'theme') ?? 'park'
			}
			let renderProperties = getRenderProperties({
				width: import.meta.env.VITE_RENDER_WIDTH,
				height: import.meta.env.VITE_RENDER_WIDTH,
				texturePack: theme,
				renderTarget: 'green',
				shadowIntensity: 0.9,
				shadowMapSize: 512,
				// shadowMapSize: 2048,
				// shadowMapType: 'VSMShadowMap',
				// shadowBlurRadius: 10,
				includeIcons: false,
				includeTexts: false
			})
			let greenData = editorCanvas.getGreenRenderData(holeId)
			//console.log(JSON.stringify(greenData))
			if (!greenData) {
				console.error('No result')
				return
			}

			let result = await holeRenderer.render(greenData, renderProperties, true)
			if (!result || !result.image || !result.data) {
				console.error('No result')
				return
			}
			let bitmap = result.image
			let context = backdropCanvas.getContext('2d')
			context.clearRect(0, 0, backdropCanvas.width, backdropCanvas.height)
			context.drawImage(bitmap, 0, 0, backdropCanvas.width, backdropCanvas.height)
			greenEditor.updatePixelsPerMeter(result.data.boundingBox.pixelsPerMeter)
		} catch (e) {
			console.error(e)
		}
		isLoading = false
		greenEditor.enable()
	}
	onDestroy(() => {
		editorState.setRendererOwner(null)
	})
	onMount(() => {
		if (visible) {
			zones = controller.getGreenZones(holeId)
			editorState.setRendererOwner('green-editor')
			holeData = controller.getHoleData(holeId)
			if (!holeData) {
				console.error('No hole data found')
				return
			}
			let greenData = controller.getGreenData(holeId) ?? []
			//guideComposer = new GuideComposer(guideComposerCanvas, scale);
			greenEditor = new GreenEditor(canvas, {
				pixelRatio: ratio,
				pixelsPerMeter: 1
			})
			greenEditor.load(greenData)
			render()
			greenEditor.on('select', (source, object) => {
				selectedObject = object
				currentColor = object.properties?.color ?? 'black'
				currentImageWidth = object.properties?.imageWidth ?? 50
				currentWidth = object.properties?.width ?? 50
				imageWidthEnabled = object.image !== undefined
				widthEndabled = object.properties?.inclination === true
				colorEnabled = !imageWidthEnabled
				textColorEnabled = object.properties?.measurements === true || object.properties?.name !== undefined
				currentTextColor = object.properties?.textColor ?? 'black'
				tidyRotationEnabled = object.getType() === 'Line'
			})
			greenEditor.on('currentTool', (source, tool, props) => {
				if (!tool) {
					currentTool = null
				} else if (tool === 'point') {
					if (props && props.name !== undefined) {
						currentTool = 'point-zone-' + props.name
					}
				} else if (tool === 'line') {
					if (props && props.inclination === true) {
						currentTool = 'line-inclination'
					} else {
						if (props.measurements) {
							currentTool = 'line-measurement'
						} else {
							currentTool = 'line-zone'
						}
					}
				}
			})
			greenEditor.on('objectAdded', (source, object) => {
				currentData = greenEditor.getData()
				for (let zone of zones) {
					if (zone.id === object.properties.id) {
						zone.disabled = true
					}
				}
				zones = [...zones]
			})
			greenEditor.on('objectDeleted', (source, object) => {
				currentData = greenEditor.getData()
				for (let zone of zones) {
					if (zone.id === object.properties.id) {
						zone.disabled = false
					}
				}
				zones = [...zones]
			})
			greenEditor.on('deselect', (source, object) => {
				colorEnabled = false
				textColorEnabled = false
				imageWidthEnabled = false
				tidyRotationEnabled = false
				selectedObject = null
			})
		}
	})
</script>

{#if visible}
	<div class="green-editor-modal">
		<div class="overlay box">
			<div class="content">
				<div class="header bg-white dark:bg-bigsteel-700/30">
					<h1>Green editor</h1>
					<div class="tools bg-black/30">
						<button
							class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center"
							selected={currentTool === 'line-zone'}
							disabled={isLoading}
							on:click={() => {
								greenEditor.activateTool('line', { color: '#ffffff', dashed: true }, false)
							}}
							tooltip="Add line">{@html getIconHtml('Line', 'dark:bg-white')}</button
						>

						<button
							disabled={isLoading}
							class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center"
							selected={currentTool === 'line-inclination'}
							on:click={() => {
								greenEditor.activateTool('line', { inclination: true, color: '#ffffff' }, false)
							}}
							tooltip="Add inclination">{@html getIconHtml('Inclination', 'dark:bg-white')}</button
						>
						<button
							class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center"
							selected={currentTool === 'line-measurement'}
							disabled={isLoading}
							on:click={() => {
								greenEditor.activateTool('line', { color: '#ffffff', dashed: false, measurements: true }, false)
							}}
							tooltip="Add measurement">{@html getIconHtml('Measurements', 'dark:bg-white')}</button
						>
					</div>
					<div class="tools bg-black/30 flex-1">
						{#if colorEnabled}
							<input
								class="color-picker"
								disabled={!colorEnabled}
								type="color"
								tooltip="Change color"
								bind:value={currentColor}
								on:input={(event) => {
									selectedObject?.setProperty('color', event.target.value)
								}}
							/>
						{/if}
						{#if textColorEnabled}
							<input
								class="color-picker"
								disabled={!textColorEnabled}
								type="color"
								tooltip="Change text color"
								bind:value={currentTextColor}
								on:input={(event) => {
									selectedObject?.setProperty('textColor', event.target.value)
								}}
							/>
						{/if}
						{#if tidyRotationEnabled}
							<button
								disabled={!tidyRotationEnabled}
								class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center"
								on:click={() => {
									selectedObject?.tidyRotation()
									greenEditor.render()
								}}
								tooltip="Tidy rotation">{@html getIconHtml('TidyRotation', 'dark:bg-white')}</button
							>
						{/if}
						{#if imageWidthEnabled}
							<input
								class="image-width"
								disabled={!imageWidthEnabled}
								tooltip="Adjust width"
								type="range"
								min="1"
								max="100"
								step="0.1"
								bind:value={currentImageWidth}
								on:input={(event) => {
									selectedObject?.setProperty('imageWidth', event.target.value)
								}}
							/>
						{/if}
						{#if widthEndabled}
							<input
								class="image-width"
								disabled={!widthEndabled}
								tooltip="Adjust width"
								type="range"
								min="10"
								max="100"
								step="0.1"
								bind:value={currentWidth}
								on:input={(event) => {
									selectedObject?.setProperty('width', event.target.value)
								}}
							/>
						{/if}
					</div>
					{#if zones && zones.length > 0}
						<div class="tools bg-black/30">
							{#each zones as item}
								<button
									disabled={isLoading}
									class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center text-white"
									selected={currentTool === 'point-zone-' + item.name || selectedObject?.properties.id === item.id}
									on:click={() => {
										let obj = greenEditor.getObject(item.id)
										if (obj) {
											greenEditor.select(obj)
										} else {
											greenEditor.activateTool('point', item, true)
										}
									}}
									tooltip="{isPointAdded(item.id) ? 'Select' : 'Add'} zone {item.name}">{item.name}</button
								>
							{/each}
						</div>
					{/if}
					<button
						class="close-button"
						on:click={() => {
							visible = false
						}}>{@html getIconHtml('Close', 'dark:bg-white')}</button
					>
				</div>
				<div class="content-center">
					<!--div class="composer-container">
						<canvas bind:this={guideComposerCanvas} width="{import.meta.env.VITE_RENDER_WIDTH * scale}" height="{import.meta.env.VITE_RENDER_HEIGHT * scale}"></canvas>
					</div-->
					<div class="green-canvas" class:invisible={isLoading}>
						<canvas bind:this={canvas} width={imageSize} height={imageSize} class="edit-canvas" style="width: {canvasSize}px; height: {canvasSize}px;"></canvas>
						<canvas bind:this={backdropCanvas} width={imageSize} height={imageSize} class="backdrop" style="width: {canvasSize}px; height: {canvasSize}px;"></canvas>
					</div>
				</div>
				<div class="buttons bg-white dark:bg-bigsteel-700/30">
					<LoadingIndicator hideWhenIdle={true} {isLoading} color="#E48B23" />
					{#if isLoading}
						<span class="text-sm">Rendering green preview...</span>
					{/if}
					<div class="spacer"></div>
					<button
						class:btn-solid-white={isDarkMode}
						class:btn-outline-blue={!isDarkMode}
						class="btn btn-sm"
						tooltip="Cancel the current rendering queue. Completed images has been saved."
						on:click={() => {
							visible = false
						}}>Cancel</button
					>
					<button
						disabled={isLoading}
						class="btn btn-sm btn-solid-blue"
						on:click={() => {
							controller.updateGreenData(holeId, greenEditor.getData())
							visible = false
						}}>Save</button
					>
				</div>
			</div>
		</div>
	</div>
{/if}

<style lang="postcss">
	.green-editor-modal {
		font-family: 'Tietoevry sans 1';
		position: absolute;
		z-index: 100;
		top: 0px;
		left: 0px;
		display: flex;
		flex-direction: column;
		flex: 1;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.5);
		justify-content: center;
		justify-items: center;
	}
	.overlay {
		overflow: hidden;
		display: flex;
		flex-direction: column;
		margin: 0px auto;
		box-sizing: border-box;
		border-radius: 16px;
	}
	.header {
		padding: 10px 20px;
		display: flex;
		gap: 16px;
		flex-direction: row;
		justify-content: center;
		align-items: center;
	}
	.green-canvas {
		width: 700px;
		height: 700px;
	}
	.content {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		flex: 1;
		gap: 0px;
		overflow-y: auto;
	}
	.backdrop {
		position: absolute;
		top: 0px;
		left: 0px;
		z-index: 2;
	}
	.edit-canvas {
		position: absolute;
		top: 0px;
		left: 0px;
		z-index: 3;
	}
	input {
		accent-color: theme('colors.blue.500');
	}
	.image-width {
		margin: auto 16px;
	}
	.color-picker {
		background-color: transparent;
		padding: 4px;
		border-radius: 8px;
	}
	.content-center {
		box-shadow: inset 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
		overflow-y: auto;
		justify-items: center;
		display: flex;
		flex-direction: row;
		flex: 1;
	}
	.content-center .green-canvas {
		position: relative;
		align-self: flex-end;
	}
	.buttons {
		display: flex;
		flex-direction: row;
		gap: 10px;
		align-items: center;
		justify-content: flex-end;
		padding: 20px;
	}
	.tools {
		height: 100%;
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		gap: 0px;
		padding: 2px;
		border-radius: 8px;
		align-items: center;
	}
	.menu-btn {
		flex: 0;
	}
	.spacer {
		flex: 1;
	}
	input[type='color'] {
		-webkit-appearance: none;
		border: none;
		width: 32px;
		height: 32px;
	}
	input:disabled {
		opacity: 0.5;
	}
	input[type='color']::-webkit-color-swatch-wrapper {
		padding: 0;
	}
	input[type='color']::-webkit-color-swatch {
		border: none;
	}
</style>
